import { Component, computed, EventEmitter, inject, input, Input, model, OnInit, Output, signal } from '@angular/core';
import { Router } from '@angular/router';
import { INotificationMapped, IUserNotification } from '../../models/user-notification';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { forkJoin, map, tap } from 'rxjs';
import { getTimeDifferenceLabel } from 'src/app/shared/utils/get-difference-time-label';
import { portalsDataKey } from 'src/app/core/constants/constants';
import { CustomerPortal } from '../../models/customer-portal';

@Component({
  selector: 'notifications',
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.scss'],
  standalone: false,
})
export class NotificationsComponent {
  private http = inject(HttpClient);
  private router = inject(Router);

  /** Event to close the notifications panel */
  @Output() closeEvent = new EventEmitter<boolean>();

  /** List of notifications to show */
  notifications = model.required<INotificationMapped[]>();

  /** Flag to show the unread notifications */
  isShowingUnreadNotifications = signal<boolean>(true);

  /** Number of unread notifications to show in the toggle */
  unreadNotificationsCount = input.required<number>();

  displayedNotifications = computed(() => {
    return this.isShowingUnreadNotifications() ? this.notifications() : this.notifications().filter((n) => !n.isRead);
  });

  /** Switch between showing all notifications and only the unread ones */
  switchNotifications() {
    this.isShowingUnreadNotifications.update((value) => !value);
  }

  /** Mark all notifications as read */
  markAllNotificationsAsRead() {
    const unreadNotifications = this.notifications().filter((n) => !n.isRead);

    if (unreadNotifications.length === 0) {
      return;
    }

    const notificationUpdateRequests = unreadNotifications.map((notification) => {
      return this.markNotificationAsRead(notification);
    });

    forkJoin([...notificationUpdateRequests]).subscribe({
      next: (updatedNotifications) => {
        console.log('All notifications successfully updated:', updatedNotifications);
      },
      error: (err) => {
        console.error('Error updating notifications:', err);
      },
    });
  }

  /** Mark a single notification as read */
  markNotificationAsRead(notification: INotificationMapped) {
    const portals: CustomerPortal[] = JSON.parse(sessionStorage.getItem(portalsDataKey));

    return this.http
      .patch<IUserNotification>(`${environment.restUrl}/v1/hub/user-notifications/${notification.id}`, { isRead: true })
      .pipe(
        map((updatedNotification) => {
          return {
            id: updatedNotification.id,
            description: updatedNotification.description,
            createdOn: getTimeDifferenceLabel(updatedNotification.createdOn),
            isRead: updatedNotification.isRead,
            redirectedUrl: updatedNotification.redirectedUrl,
            title: portals.find((portal) => portal.addonName === updatedNotification.serviceAddon)?.name,
            icon: portals.find((portal) => portal.addonName === updatedNotification.serviceAddon)?.icon,
          };
        }),
        tap((updatedNotification) => {
          const index = this.notifications().findIndex((n) => n.id === updatedNotification.id);

          if (index !== -1) {
            const updatedNotifications = [...this.notifications()];
            updatedNotifications[index] = updatedNotification;
            this.notifications.set(updatedNotifications);
          }
        }),
      );
  }

  onNotificationClick(event: Event, notification: INotificationMapped) {
    event.preventDefault();
    this.markNotificationAsRead(notification).subscribe();

    if (!notification.redirectedUrl) {
      return;
    }

    // If the redirectedUrl starts with http or https, it's an absolute path - open it in a new tab.
    if (/^https?:\/\//i.test(notification.redirectedUrl)) {
      window.open(notification.redirectedUrl, '_blank');
      // Otherwise, it's a relative path - navigate within the app.
    } else {
      this.router.navigateByUrl(notification.redirectedUrl);
    }
  }

  /** Close the notifications panel */
  closeNotifications() {
    this.closeEvent.emit(false);
  }
}
